import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Truck, Search, Filter, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { Shipment, PaginationMeta } from '../types'
import { formatDate, getShipmentStatusLabel, getShipmentStatusColor } from '../utils/helpers'
import Pagination from '../components/Pagination'

export default function Shipments() {
  const { t } = useTranslation()
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [filteredShipments, setFilteredShipments] = useState<Shipment[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortField, setSortField] = useState<string | null>('date_creation')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const loadShipments = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getShipments({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setShipments(list)
      setFilteredShipments(list)
      setPagination(pag)
      setError(null)
    } catch (err: unknown) {
      console.error('Erreur chargement expéditions:', err)
      setError(getApiErrorKey(err, 'shipments.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, limit])

  const filterAndSortShipments = useCallback(() => {
    let filtered = shipments

    if (searchTerm) {
      filtered = filtered.filter(
        (shipment) =>
          shipment.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
          shipment.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      const statusNum = parseInt(statusFilter, 10)
      filtered = filtered.filter((shipment) => {
        const s =
          typeof shipment.status === 'string'
            ? parseInt(shipment.status, 10)
            : Number(shipment.status)
        return s === statusNum
      })
    }

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const va = (a as unknown as Record<string, unknown>)[sortField] ?? ''
        const vb = (b as unknown as Record<string, unknown>)[sortField] ?? ''
        if (typeof va === 'number' && typeof vb === 'number')
          return sortDir === 'asc' ? va - vb : vb - va
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va))
      })
    }

    setFilteredShipments(filtered)
  }, [shipments, searchTerm, statusFilter, sortField, sortDir])

  useEffect(() => {
    loadShipments()
  }, [loadShipments])
  useEffect(() => {
    filterAndSortShipments()
  }, [filterAndSortShipments])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300 inline ml-1" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-primary-600 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-primary-600 inline ml-1" />
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium mb-2">{t(error)}</p>
          <button
            onClick={loadShipments}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <p className="text-sm font-medium text-gray-600">{t('shipments.totalShipments')}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">
          {pagination ? pagination.total : filteredShipments.length}
        </p>
      </div>

      {!pagination && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('shipments.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none appearance-none bg-white"
              >
                <option value="all">{t('common.allStatuses')}</option>
                <option value="0">{t('shipments.statusDraft')}</option>
                <option value="1">{t('shipments.statusValidated')}</option>
                <option value="2">{t('shipments.statusClosed')}</option>
                <option value="-1">{t('shipments.statusCancelled')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredShipments.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>{t('shipments.notFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {filteredShipments.map((shipment) => (
                <li key={shipment.id} className="p-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {pagination && (
                        <p className="text-xs text-gray-500 truncate">
                          {shipment.customer_name ?? '—'}
                        </p>
                      )}
                      <Link
                        to={`/shipments/${shipment.id}${shipment._erp_id ? `?erp_id=${shipment._erp_id}` : ''}`}
                        className="font-medium text-primary-600 hover:text-primary-700 text-sm"
                      >
                        {shipment.ref}
                      </Link>
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getShipmentStatusColor(shipment.status)}`}
                    >
                      {getShipmentStatusLabel(shipment.status, t)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{formatDate(shipment.date_creation)}</span>
                    {shipment.delivery_date && (
                      <span>
                        {t('shipments.colDateDelivery')} : {formatDate(shipment.delivery_date)}
                      </span>
                    )}
                  </div>
                  {shipment.tracking_number && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="font-mono">{shipment.tracking_number}</span>
                      {shipment.tracking_url && (
                        <a
                          href={shipment.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {pagination && (
                      <th
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('customer_name')}
                      >
                        {t('common.client')} <SortIcon field="customer_name" />
                      </th>
                    )}
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('ref')}
                    >
                      {t('shipments.colRef')} <SortIcon field="ref" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date_creation')}
                    >
                      {t('shipments.colDateCreation')} <SortIcon field="date_creation" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('delivery_date')}
                    >
                      {t('shipments.colDateDelivery')} <SortIcon field="delivery_date" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('shipments.colTracking')}
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      {t('shipments.colStatus')} <SortIcon field="status" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredShipments.map((shipment) => (
                    <tr key={shipment.id} className="hover:bg-gray-50 transition-colors">
                      {pagination && (
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium whitespace-nowrap">
                          {shipment.customer_name ?? '—'}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <Link
                          to={`/shipments/${shipment.id}${shipment._erp_id ? `?erp_id=${shipment._erp_id}` : ''}`}
                          className="font-medium text-primary-600 hover:text-primary-700"
                        >
                          {shipment.ref}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(shipment.date_creation)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {shipment.delivery_date ? formatDate(shipment.delivery_date) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {shipment.tracking_number ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900 font-mono">
                              {shipment.tracking_number}
                            </span>
                            {shipment.tracking_url && (
                              <a
                                href={shipment.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700"
                                title={t('shipments.trackPackage')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getShipmentStatusColor(shipment.status)}`}
                        >
                          {getShipmentStatusLabel(shipment.status, t)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {pagination && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
